import { ReactElement, useEffect, useMemo } from 'react'
import { pdfCmOffsetToWorldMeters } from '@constants'
import { DoubleSide, MeshLambertMaterial, PlaneGeometry, TextureLoader } from 'three'
import { useLoader } from '@react-three/fiber'
import { applyForegroundBackgroundTransparency, computeStoreyImagePlaneSize } from './utils'

interface StoreyImageProps {
  scale: number
  offsetX: number
  offsetY: number
  rotation: number
  foregroundTransparency: number
  backgroundTransparency: number
  paperFormat: string
  /** Horizontal width in cm at 1:1; used when `paperFormat` is `custom`. */
  paperWidthCm?: number
  url: string
  z?: number
}

const FloorplanImage = ({
  scale,
  offsetX,
  offsetY,
  rotation,
  foregroundTransparency,
  backgroundTransparency,
  paperFormat,
  paperWidthCm,
  url,
  z = -0.05,
}: StoreyImageProps): ReactElement => {
  const texture = useLoader(TextureLoader, url)
  const sourceImage = texture.image instanceof HTMLImageElement ? texture.image : null
  const imageDimensions = useMemo(() => {
    if (!sourceImage?.complete || !sourceImage.naturalWidth || !sourceImage.naturalHeight) {
      return null
    }

    return { width: sourceImage.naturalWidth, height: sourceImage.naturalHeight }
  }, [sourceImage])

  const processedTexture = useMemo(() => {
    if (!sourceImage || !imageDimensions) return texture

    if (foregroundTransparency === 0 && backgroundTransparency === 0) {
      return texture
    }

    return applyForegroundBackgroundTransparency(
      sourceImage,
      texture,
      foregroundTransparency,
      backgroundTransparency,
    )
  }, [texture, sourceImage, foregroundTransparency, backgroundTransparency, imageDimensions])

  useEffect(() => {
    if (processedTexture === texture) return
    return () => processedTexture.dispose()
  }, [processedTexture, texture])

  const material = useMemo(
    () =>
      new MeshLambertMaterial({
        map: processedTexture,
        transparent: true,
        opacity: 1,
        side: DoubleSide,
      }),
    [processedTexture],
  )

  const planeSize = useMemo(() => {
    if (!imageDimensions) {
      return null
    }

    return computeStoreyImagePlaneSize({
      imageWidthPx: imageDimensions.width,
      imageHeightPx: imageDimensions.height,
      paperFormat,
      paperWidthCm,
    })
  }, [imageDimensions, paperFormat, paperWidthCm])

  const geometry = useMemo(() => {
    if (!planeSize) return null
    return new PlaneGeometry(planeSize.width, planeSize.height)
  }, [planeSize])

  const rotationRad = (rotation * Math.PI) / 180
  const worldOffsetX = pdfCmOffsetToWorldMeters(offsetX, scale)
  const worldOffsetY = pdfCmOffsetToWorldMeters(offsetY, scale)

  if (!planeSize || !geometry) {
    return <></>
  }

  return (
    <group
      position={[worldOffsetX, worldOffsetY, z]}
      rotation={[0, 0, rotationRad]}
      scale={[scale, scale, 1]}
    >
      <mesh
        material={material}
        geometry={geometry}
        position={[planeSize.width / 2, planeSize.height / 2, 0]}
      />
    </group>
  )
}

export default FloorplanImage
